import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SlidersHorizontal, X } from 'lucide-react';
import { apiGet } from '../lib/api';
import ProductCard from '../components/ProductCard';
import Reveal from '../components/Reveal';

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [result, setResult] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  const category = params.get('category') || '';
  const search = params.get('search') || '';
  const urlBrands = params.get('brand') ? [params.get('brand')] : [];
  const [sort, setSort] = useState('featured');
  const [maxPrice, setMaxPrice] = useState(2000);
  const [inStock, setInStock] = useState(false);

  useEffect(() => {
    apiGet('/categories').then(setCategories).catch(() => {});
    apiGet('/brands').then(setBrands).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const p = { sort, limit: 60 };
    if (category) p.category = category;
    if (search) p.search = search;
    if (urlBrands.length) p.brand = urlBrands[0];
    if (inStock) p.in_stock = 'true';
    if (maxPrice < 2000) p.max_price = maxPrice;
    apiGet('/products', p)
      .then(setResult)
      .catch(() => setResult({ items: [], total: 0 }))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, search, sort, maxPrice, inStock, params]);

  const setParam = (key, val) => {
    const next = new URLSearchParams(params);
    if (val) next.set(key, val);
    else next.delete(key);
    setParams(next);
  };

  const clearAll = () => {
    setParams(new URLSearchParams());
    setMaxPrice(2000);
    setInStock(false);
    setSort('featured');
  };

  const hasFilters = category || search || urlBrands.length || maxPrice < 2000 || inStock;
  const activeCat = categories.find((c) => c.slug === category);

  const FilterPanel = () => (
    <div className="space-y-8">
      <div>
        <h4 className="font-display font-semibold uppercase text-sm text-forest mb-3">Category</h4>
        <ul className="space-y-1.5">
          <li>
            <button
              data-testid="filter-category-all"
              onClick={() => setParam('category', '')}
              className={`text-sm w-full text-left px-3 py-1.5 rounded-lg transition ${!category ? 'bg-forest text-cream font-semibold' : 'hover:bg-silver'}`}
            >
              All Categories
            </button>
          </li>
          {categories.map((c) => (
            <li key={c.id}>
              <button
                data-testid={`filter-category-${c.slug}`}
                onClick={() => setParam('category', c.slug)}
                className={`text-sm w-full text-left px-3 py-1.5 rounded-lg transition flex justify-between ${category === c.slug ? 'bg-forest text-cream font-semibold' : 'hover:bg-silver'}`}
              >
                <span>{c.name}</span>
                <span className="text-xs opacity-60">{c.product_count}</span>
              </button>
            </li>
          ))}
        </ul>
      </div>
      <div>
        <h4 className="font-display font-semibold uppercase text-sm text-forest mb-3">Tractor Brand</h4>
        <div className="flex flex-wrap gap-2">
          {brands.map((b) => (
            <button
              key={b.id}
              data-testid={`filter-brand-${b.slug}`}
              onClick={() => setParam('brand', urlBrands[0] === b.name ? '' : b.name)}
              className={`font-mono text-[11px] uppercase tracking-wide px-3 py-1.5 rounded-full border transition ${
                urlBrands[0] === b.name
                  ? 'bg-flame border-flame text-white'
                  : 'border-emerald-950/20 text-ink hover:border-flame'
              }`}
            >
              {b.name}
            </button>
          ))}
        </div>
      </div>
      <div>
        <h4 className="font-display font-semibold uppercase text-sm text-forest mb-3">
          Max Price: <span data-testid="price-filter-value" className="font-mono text-flame">${maxPrice.toLocaleString()}</span>
        </h4>
        <input
          data-testid="price-range-slider"
          type="range"
          min="20"
          max="2000"
          step="10"
          value={maxPrice}
          onChange={(e) => setMaxPrice(Number(e.target.value))}
          className="w-full"
        />
      </div>
      <label className="flex items-center gap-3 cursor-pointer select-none">
        <input
          data-testid="in-stock-toggle"
          type="checkbox"
          checked={inStock}
          onChange={(e) => setInStock(e.target.checked)}
          className="w-4 h-4 accent-[#FF5722]"
        />
        <span className="text-sm text-ink font-medium">In stock only</span>
      </label>
      {hasFilters && (
        <button
          data-testid="clear-filters-button"
          onClick={clearAll}
          className="inline-flex items-center gap-1.5 text-sm text-flame font-semibold hover:underline"
        >
          <X className="w-3.5 h-3.5" /> Clear all filters
        </button>
      )}
    </div>
  );

  return (
    <div className="bg-cream min-h-screen">
      <div className="bg-forest noise relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <p className="font-mono text-[11px] uppercase tracking-widest2 text-flame mb-3">The parts counter</p>
          <h1 className="font-display font-extrabold uppercase tracking-tight text-cream text-4xl sm:text-5xl">
            {activeCat ? activeCat.name : search ? `Results for "${search}"` : urlBrands[0] ? `${urlBrands[0]} Parts` : 'Shop All Parts'}
          </h1>
          <p className="text-cream/50 mt-2 text-sm">
            {activeCat?.blurb || 'Dependable components at competitive prices, shipped fast across the US.'}
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid lg:grid-cols-[260px_1fr] gap-10">
        <aside className="hidden lg:block">
          <div className="sticky top-24 bg-white border border-emerald-950/10 rounded-2xl p-6">
            <FilterPanel />
          </div>
        </aside>

        <div>
          <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
            <p data-testid="results-count" className="text-sm text-sub">
              <span className="font-bold text-forest">{result.total}</span> parts found
            </p>
            <div className="flex items-center gap-2">
              <button
                data-testid="mobile-filters-toggle"
                onClick={() => setShowFilters(!showFilters)}
                className="lg:hidden inline-flex items-center gap-2 text-sm font-semibold border border-emerald-950/20 rounded-full px-4 py-2"
              >
                <SlidersHorizontal className="w-4 h-4" /> Filters
              </button>
              <select
                data-testid="sort-select"
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                className="text-sm border border-emerald-950/20 rounded-full px-4 py-2 bg-white focus:outline-none focus:border-flame"
              >
                <option value="featured">Sort: Featured</option>
                <option value="price_asc">Price: Low to High</option>
                <option value="price_desc">Price: High to Low</option>
                <option value="rating">Top Rated</option>
                <option value="name">Name A–Z</option>
              </select>
            </div>
          </div>

          {showFilters && (
            <div className="lg:hidden mb-6 bg-white border border-emerald-950/10 rounded-2xl p-6">
              <FilterPanel />
            </div>
          )}

          {loading ? (
            <div data-testid="shop-loading" className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="bg-white rounded-2xl overflow-hidden border border-emerald-950/10 animate-pulse">
                  <div className="aspect-square bg-silver" />
                  <div className="p-5 space-y-3">
                    <div className="h-3 bg-silver rounded w-1/3" />
                    <div className="h-5 bg-silver rounded w-3/4" />
                    <div className="h-6 bg-silver rounded w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : result.items.length === 0 ? (
            <div data-testid="shop-empty-state" className="text-center py-24 bg-white border border-emerald-950/10 rounded-2xl">
              <p className="font-display font-bold uppercase text-3xl text-forest">No parts match</p>
              <p className="text-sm text-sub mt-2 mb-6">Try clearing a filter or searching a different part number.</p>
              <button onClick={clearAll} className="font-display uppercase font-semibold bg-forest text-cream px-6 py-3 rounded-full hover:bg-flame transition">
                Reset the counter
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
              {result.items.map((p, i) => (
                <ProductCard key={p.id} p={p} index={i} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
